import { useEffect, useRef, useState } from "react";
import { apiRequest, createIdempotencyKey } from "@kira/api-client";
import type { Order, OrderActionInput } from "@kira/contracts";
import { Button } from "@kira/ui";

export type OrderPanelAction = "details" | "confirm" | "refund" | "review" | "handle-refund";
export function OrderActionPanel({ initialOrder, action, onUpdated }: { initialOrder: Order; action: OrderPanelAction; onUpdated: (order: Order) => void }) {
  const [order, setOrder] = useState(initialOrder);
  const [reason, setReason] = useState("");
  const [rating, setRating] = useState(5);
  const [approve, setApprove] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const attempt = useRef<{ body: string; key: string } | null>(null);
  useEffect(() => {
    let active = true;
    void apiRequest<Order>(`/api/market/orders/${encodeURIComponent(initialOrder.id)}`).then(order => { if (active) setOrder(order); }).catch(error => { if (active) setError(error instanceof Error ? error.message : "订单加载失败"); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [initialOrder.id]);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (action === "details") return;
    const input: OrderActionInput = action === "confirm" ? { action: "confirm" }
      : action === "review" ? { action: "review", rating, content: reason }
      : action === "refund" ? { action: "refund", reason }
      : { action: approve ? "approve-refund" : "reject-refund", reason };
    const body = JSON.stringify(input);
    if (attempt.current?.body !== body) attempt.current = { body, key: createIdempotencyKey("order-action") };
    setBusy(true); setError("");
    try {
      const updated = await apiRequest<Order>(`/api/market/orders/${encodeURIComponent(order.id)}/action`, { method: "POST", body: input, idempotencyKey: attempt.current.key });
      setOrder(updated); setDone(true); onUpdated(updated);
    } catch (error) { setError(error instanceof Error ? error.message : "操作失败，请重试"); }
    finally { setBusy(false); }
  }
  return <div className="order-action-panel">
    <p className="chat-semantics">本地模拟订单，收货与退款操作不会产生真实资金转移。</p>
    <p className="order-action-id">订单 {order.id}</p>
    {order.items.map(item => <p key={item.productId}>{item.title} × {item.quantity}</p>)}
    <p>实付 ¥{(order.totalCents / 100).toFixed(2)}（含运费 ¥{(order.shippingCents / 100).toFixed(2)}）</p>
    {order.trackingNumber ? <p>物流：{order.carrier} · {order.trackingNumber}<br/><small>此处展示卖家填写的运单信息，未接入承运商实时轨迹。</small></p> : <p>尚无发货信息</p>}
    {order.refundReason ? <p>退款申请：{order.refundReason}</p> : null}
    {order.refundResolution ? <p>处理说明：{order.refundResolution}</p> : null}
    {order.review ? <p>已评价：{order.review.rating} 星 · {order.review.content}</p> : null}
    {error ? <p role="alert" className="form-error">{error}</p> : null}
    {done ? <p role="status">操作已完成，订单状态已更新。</p> : action !== "details" ? <form onSubmit={event => void submit(event)}>
      {action === "confirm" ? <p>请确认已收到订单中的全部商品。确认后可提交评价。</p> : <>
        {action === "refund" ? <p>仅支持发货前、商品允许的整单退款；拼车须在截止前申请。提交后暂停发货，等待卖家处理。</p> : null}
        {action === "handle-refund" ? <label>处理结果<select disabled={busy} value={approve ? "approve" : "reject"} onChange={event => setApprove(event.target.value === "approve")}><option value="approve">同意退款</option><option value="reject">驳回申请，恢复待发货</option></select></label> : null}
        {action === "review" ? <label>评分<select disabled={busy} value={rating} onChange={event => setRating(Number(event.target.value))}>{[5,4,3,2,1].map(value => <option key={value} value={value}>{value} 星</option>)}</select></label> : null}
        <label>{action === "review" ? "评价内容" : "原因或处理说明"}<textarea required minLength={2} maxLength={500} disabled={busy} value={reason} onChange={event => setReason(event.target.value)} /></label>
      </>}
      <Button type="submit" className="k-button--accent" disabled={loading || busy || (action === "refund" && !order.refundEligible)}>{busy ? "处理中…" : "确认提交"}</Button>
    </form> : null}
  </div>;
}
